import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useRememberListUrl } from '../lib/listReturn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, canDelete, can, ROLE } from '../lib/auth';
import { generateJobCardPdf } from '../lib/generateJobCardPdf';

interface DispatchLite { id: string; jobId: string; dispatchOrderDate: string; totalBoxDispatch: string; invoiceNo?: string }

/**
 * A job's dispatches, in the Reopen dialog.  One sent by mistake is
 * cancelled here: it is removed, the job goes back to In Progress and is
 * reopened for Planning (however old, every pass), and it can be dispatched
 * properly later.  Reopen alone would keep the dispatch on record, and a
 * real dispatch later would find nothing left to send.
 */
function JobDispatches({ jobId, onCancelled }: { jobId: string; onCancelled: () => void }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['job-dispatches', jobId],
    queryFn: () => api<{ rows: DispatchLite[] }>(`/api/dispatches?q=${encodeURIComponent(jobId)}&pageSize=200`),
  });
  const rows = (list.data?.rows ?? []).filter((d) => d.jobId === jobId);
  const cancel = useMutation({
    mutationFn: (dispatchId: string) =>
      api('/api/flow/cancel-dispatch', { method: 'POST', body: JSON.stringify({ dispatchId, reopen: true }) }),
    onSuccess: async () => {
      await list.refetch();
      for (const k of ['jobs', 'planner-jobs', 'flow-queue']) qc.invalidateQueries({ queryKey: [k] });
      onCancelled();
    },
    onError: (e: Error) => alert('Cancel failed: ' + e.message),
  });
  if (list.isLoading) return <p className="text-xs text-slate-500 mb-3">Loading its dispatches…</p>;
  if (!rows.length) return null;
  return (
    <div className="mb-4 rounded border border-amber-200 bg-amber-50 p-3">
      <div className="text-xs font-semibold text-amber-900">Dispatched by mistake? Cancel the dispatch instead.</div>
      <p className="mt-0.5 mb-2 text-[11px] text-amber-800">
        Cancelling removes it and puts the job back to In Progress — it shows in Planning and can be dispatched
        again. Reopen keeps the dispatch on record: use it for a reprint of a job really sent.
      </p>
      <ul className="space-y-1">
        {rows.map((d) => (
          <li key={d.id} className="flex items-center justify-between gap-2 text-xs">
            <span>
              <span className="font-mono font-semibold">{d.id}</span>
              {' · '}{d.dispatchOrderDate || '—'}
              {' · '}<b>{(Number(d.totalBoxDispatch) || 0).toLocaleString('en-IN')}</b> boxes
              {d.invoiceNo ? <> · Inv {d.invoiceNo}</> : null}
            </span>
            <button
              className="font-medium text-red-600 hover:underline disabled:opacity-50"
              disabled={cancel.isPending}
              onClick={() => {
                if (confirm(`Cancel dispatch ${d.id} (${d.totalBoxDispatch} boxes)?\n\nIt is removed, and the job goes back to In Progress.`)) cancel.mutate(d.id);
              }}
            >
              {cancel.isPending && cancel.variables === d.id ? 'Cancelling…' : 'Cancel dispatch'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Inline Save-PDF button with per-row loading state */
function SavePdfButton({ jobId }: { jobId: string }) {
  const [busy, setBusy] = useState(false);
  async function handleSave() {
    if (busy) return;
    setBusy(true);
    try {
      await generateJobCardPdf(jobId);
    } catch (err) {
      alert('PDF generation failed: ' + (err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      onClick={handleSave}
      disabled={busy}
      className="text-emerald-700 hover:underline text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      title="Download Job Card as PDF"
    >
      {busy ? 'Saving…' : 'Save'}
    </button>
  );
}

interface JobRow {
  id: string; poRefNumber: string; productId: string; jobName: string;
  jobOrderDate: string; totalBoxes: string; ups: string; jobStatus: string;
  totalSheetWithWastage: string; designNo: string; clientName: string;
  linkedPoCount?: number;
}
interface ListResp { rows: JobRow[]; total: number; page: number; pageSize: number }

const PAGE_SIZE = 50;

export function JobOrdersPage() {
  useRememberListUrl();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1'));
  const q = params.get('q') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const [search, setSearch] = useState(q);
  const [fromI, setFromI] = useState(from);
  const [toI, setToI] = useState(to);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['jobs', { page, q, from, to }],
    queryFn: () => {
      const sp = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (q) sp.set('q', q);
      if (from) sp.set('from', from);
      if (to) sp.set('to', to);
      return api<ListResp>(`/api/job-orders?${sp.toString()}`);
    },
  });

  const { user } = useAuth();
  const del = useMutation({
    mutationFn: (ids: string[]) =>
      api('/api/job-orders/delete', { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => { setSelected(new Set()); qc.invalidateQueries({ queryKey: ['jobs'] }); },
    onError: (e: Error) => {
      const m = e.message;
      if (m.startsWith('has_dispatch_records:')) alert(`Cannot delete — dispatches exist for ${m.split(':')[1]}.`);
      else if (m.startsWith('has_production_records:')) alert(`Cannot delete — production entries exist for ${m.split(':')[1]}.`);
      else alert(m);
    },
  });

  const navigate = useNavigate();
  const [reissueTarget, setReissueTarget] = useState<string | null>(null);
  const [reissueCategory, setReissueCategory] = useState('');
  const [reissueOtherText, setReissueOtherText] = useState('');
  const [closeTarget, setCloseTarget] = useState<string | null>(null);
  const [closeReason, setCloseReason] = useState('');

  const closeJob = useMutation({
    mutationFn: (v: { jobId: string; reason: string }) =>
      api('/api/flow/close-job', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: () => { setCloseTarget(null); setCloseReason(''); qc.invalidateQueries({ queryKey: ['jobs'] }); },
    onError: (e: Error) => alert('Close failed: ' + e.message),
  });
  // Super Admin: a Printed / Closed / Dispatched job card back to In Progress —
  // Planning lists it again, every pass of it.
  const [reopenTarget, setReopenTarget] = useState<{ id: string; status: string } | null>(null);
  const [reopenReason, setReopenReason] = useState('');
  // A dispatch cancelled from the dialog has already put the job back to In Progress.
  const [reopenDone, setReopenDone] = useState(false);
  const closeReopen = () => { setReopenTarget(null); setReopenReason(''); setReopenDone(false); };
  const reopenJob = useMutation({
    mutationFn: (v: { jobId: string; reason: string }) =>
      api('/api/flow/reopen-job', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: () => {
      closeReopen();
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['planner-jobs'] });
    },
    onError: (e: Error) => alert(e.message === 'job_not_closed'
      ? 'This job card is already open.'
      : 'Reopen failed: ' + e.message),
  });
  const [versionsTarget, setVersionsTarget] = useState<string | null>(null);

  const versions = useQuery({
    enabled: !!versionsTarget,
    queryKey: ['job-history', versionsTarget],
    queryFn: () => api<Array<{
      History_ID: number; Changed_On: string | null; UserName: string | null;
      Change_Reason: string | null; Total_Boxes: string | null;
    }>>(`/api/job-orders/${encodeURIComponent(versionsTarget!)}/history`),
  });

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    const p = new URLSearchParams();
    if (search) p.set('q', search);
    if (fromI) p.set('from', fromI);
    if (toI) p.set('to', toI);
    p.set('page', '1');
    setParams(p);
  }
  function goPage(next: number) {
    const p = new URLSearchParams(params);
    p.set('page', String(next));
    setParams(p);
  }
  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-brand">Job Orders</h1>
          <p className="text-xs text-slate-500">{total.toLocaleString()} active</p>
          {user?.rollId !== ROLE.SuperAdmin && can(user, 'EDIT_JOB') && <DirectEditsLeft />}
        </div>
        {can(user, 'CREATE_JOB') && (
          <Link to="/job-orders/new" className="btn-primary">+ New Job Order</Link>
        )}
      </div>

      <form className="flex flex-wrap items-end gap-2" onSubmit={applySearch}>
        <div className="flex-1 min-w-[240px]">
          <label className="label">Search</label>
          <input className="input" placeholder="Job ID, PO #, Job Name, Design No, or size 70 x 56 x 76…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div>
          <label className="label">From</label>
          <input type="date" className="input" value={fromI} onChange={(e) => setFromI(e.target.value)} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input" value={toI} onChange={(e) => setToI(e.target.value)} />
        </div>
        <button className="btn-primary" type="submit">Search</button>
        {(q || from || to) && (
          <button type="button" className="btn-ghost"
            onClick={() => { setSearch(''); setFromI(''); setToI(''); setParams({}); }}>
            Reset
          </button>
        )}
        {canDelete(user) && selected.size > 0 && (
          <button type="button" className="btn-danger" disabled={del.isPending}
            onClick={() => { if (confirm(`Delete ${selected.size}?`)) del.mutate(Array.from(selected)); }}>
            Delete {selected.size}
          </button>
        )}
      </form>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2">Job ID</th>
                <th className="px-3 py-2">Client Name</th>
                <th className="px-3 py-2">Job Name</th>
                <th className="px-3 py-2">Design No</th>
                <th className="px-3 py-2">JO Date</th>
                <th className="px-3 py-2 text-right">Total Boxes</th>
                <th className="px-3 py-2 text-right">Ups</th>
                <th className="px-3 py-2 text-right">Sheets+W</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && <tr><td colSpan={11} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {list.isError && <tr><td colSpan={11} className="p-8 text-center text-red-600">Failed: {(list.error as Error).message}</td></tr>}
              {!list.isLoading && rows.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-slate-500">No records.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {(r as any).versionCount > 0 ? (
                      <button
                        onClick={() => setVersionsTarget(r.id)}
                        className="text-brand hover:underline font-mono"
                        title={`${(r as any).versionCount} previous version(s)`}
                      >{r.id} <span className="text-[10px] text-amber-600 font-semibold">V{(r as any).versionCount + 1}</span></button>
                    ) : r.id}
                    {(r.linkedPoCount ?? 0) > 1 && (
                      <span className="ml-1 inline-block rounded bg-blue-100 px-1 text-[10px] font-semibold text-blue-700" title={`Clubbed: ${r.linkedPoCount} POs`}>
                        {r.linkedPoCount}PO
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.clientName}</td>
                  <td className="px-3 py-2">{r.jobName}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{r.designNo}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{r.jobOrderDate}</td>
                  <td className="px-3 py-2 text-right">{r.totalBoxes}</td>
                  <td className="px-3 py-2 text-right">{r.ups}</td>
                  <td className="px-3 py-2 text-right">{r.totalSheetWithWastage}</td>
                  <td className="px-3 py-2 text-xs">{r.jobStatus}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      {/* Edit offers Club Order / Quantity (no approval for
                          anyone) or a full edit (Super Admin directly; VPPL
                          with GM's approval or a direct edit). */}
                      {can(user, 'EDIT_JOB') && <EditAction jobId={r.id} superAdmin={user?.rollId === ROLE.SuperAdmin} />}
                      {/* Everyone else (CRM, GM, …) gets the same form read-only. */}
                      {user?.rollId !== ROLE.SuperAdmin && !can(user, 'EDIT_JOB') && (
                        <Link to={`/job-orders/${encodeURIComponent(r.id)}/edit`} className="text-brand hover:underline text-sm">View</Link>
                      )}
                      <Link to={`/job-orders/${encodeURIComponent(r.id)}/print`} className="text-red-600 hover:underline text-sm">Card</Link>
                      <SavePdfButton jobId={r.id} />
                      {can(user, 'EDIT_JOB') && <button
                        onClick={() => setReissueTarget(r.id)}
                        className="text-amber-600 hover:underline text-sm"
                      >Reissue</button>}
                      {user?.rollId === ROLE.SuperAdmin && r.jobStatus !== 'Closed' && (
                        <button onClick={() => setCloseTarget(r.id)} className="text-red-600 hover:underline text-sm">Close</button>
                      )}
                      {user?.rollId === ROLE.SuperAdmin && ['Printed', 'Closed', 'Dispatched', 'In Progress'].includes((r.jobStatus ?? '').trim()) && (
                        <button onClick={() => setReopenTarget({ id: r.id, status: r.jobStatus })}
                                title="Back to In Progress — shows in Planning again, every pass"
                                className="text-emerald-700 hover:underline text-sm">Reopen</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">Page {page} of {totalPages.toLocaleString()}</div>
        <div className="flex gap-2">
          <button className="btn-ghost" disabled={page <= 1} onClick={() => goPage(page - 1)}>‹ Prev</button>
          <button className="btn-ghost" disabled={page >= totalPages} onClick={() => goPage(page + 1)}>Next ›</button>
        </div>
      </div>

      {reissueTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-brand mb-3">Reissue Job Card</h2>
            <p className="text-sm text-slate-600 mb-2">Job: <span className="font-mono">{reissueTarget}</span></p>
            <label className="label">Reason for Reissue <span className="text-red-500">*</span></label>
            <select
              className="input w-full"
              value={reissueCategory}
              onChange={(e) => { setReissueCategory(e.target.value); if (e.target.value !== 'Other') setReissueOtherText(''); }}
            >
              <option value="">— Select Reason —</option>
              <option value="Against Rejection - In House">Against Rejection - In House</option>
              <option value="Against Rejection - Client">Against Rejection - Client</option>
              <option value="Short Quantity">Short Quantity</option>
              {/* Club Order (attach another PO / change the quantity) is not
                  a reissue: it is under Edit. */}
              {/* Change Spec closes this job and spawns a fresh one with a
                  new serial number, carrying the same PO forward.  The
                  spec-change reason is stamped on the new job card and
                  shows on its print. */}
              <option value="Change Spec">Change Spec + Create New Job Card</option>
              <option value="Other">Other</option>
            </select>
            {(reissueCategory === 'Other' || reissueCategory === 'Change Spec') && (
              <textarea
                className="input w-full h-20 mt-2"
                value={reissueOtherText}
                onChange={(e) => setReissueOtherText(e.target.value)}
                placeholder={reissueCategory === 'Change Spec' ? 'What is changing? (e.g. Client asked for 8-up instead of 6-up)' : 'Enter reason…'}
              />
            )}
            {reissueCategory === 'Change Spec' && (
              <p className="text-xs text-slate-500 mt-2">
                The current job card will be closed and a new job card with a fresh serial number will open,
                pre-filled from this one. You can change anything — UPS, sheet size, die, machine — before saving.
              </p>
            )}
            <p className="text-xs text-slate-500 mt-2">
              Attaching another PO or changing the quantity / wastage is not a reissue: use <b>Edit → Club Order / Quantity</b>.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button className="btn-ghost" onClick={() => { setReissueTarget(null); setReissueCategory(''); setReissueOtherText(''); }}>Cancel</button>
              <button
                className="btn-primary"
                disabled={
                  !reissueCategory
                  || ((reissueCategory === 'Other' || reissueCategory === 'Change Spec') && !reissueOtherText.trim())
                }
                onClick={() => {
                  const reason = (reissueCategory === 'Other' || reissueCategory === 'Change Spec')
                    ? reissueOtherText.trim()
                    : reissueCategory;
                  const target = reissueTarget;
                  const isSpec = reissueCategory === 'Change Spec';
                  setReissueTarget(null);
                  setReissueCategory('');
                  setReissueOtherText('');
                  if (isSpec) {
                    // Fresh Job Order form pre-filled from the parent; server
                    // closes the parent + transfers the PO on save.
                    navigate(`/job-orders/new?respawnFrom=${encodeURIComponent(target)}&reason=${encodeURIComponent(reason)}`);
                  } else {
                    navigate(`/job-orders/${encodeURIComponent(target)}/edit?reissue=1&reason=${encodeURIComponent(reason)}`);
                  }
                }}
              >{reissueCategory === 'Change Spec' ? 'Continue to New Job Card' : 'Continue to Edit'}</button>
            </div>
          </div>
        </div>
      )}

      {closeTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-red-700 mb-3">Close Job Card</h2>
            <p className="text-sm text-slate-600 mb-2">Job: <span className="font-mono">{closeTarget}</span></p>
            <p className="text-xs text-slate-500 mb-3">This will remove the job from all queues.</p>
            <label className="label">Reason <span className="text-red-500">*</span></label>
            <textarea className="input w-full h-20" value={closeReason} onChange={(e) => setCloseReason(e.target.value)} placeholder="Why is this being closed?" />
            <div className="flex justify-end gap-2 mt-4">
              <button className="btn-ghost" onClick={() => { setCloseTarget(null); setCloseReason(''); }}>Cancel</button>
              <button className="btn-danger" disabled={!closeReason.trim() || closeJob.isPending}
                onClick={() => closeJob.mutate({ jobId: closeTarget, reason: closeReason.trim() })}>
                {closeJob.isPending ? 'Closing…' : 'Close Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reopenTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-emerald-800 mb-3">Reopen Job Card</h2>
            <p className="text-sm text-slate-600 mb-2">
              Job: <span className="font-mono">{reopenTarget.id}</span> · now <b>{reopenDone ? 'In Progress' : reopenTarget.status}</b>
            </p>
            {reopenDone && (
              <p className="mb-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                Dispatch cancelled — the job is back to In Progress and shows in Planning again.
              </p>
            )}
            {['Dispatched', 'In Progress'].includes((reopenTarget.status ?? '').trim()) && (
              <JobDispatches jobId={reopenTarget.id} onCancelled={() => setReopenDone(true)} />
            )}
            {reopenDone ? (
              <div className="flex justify-end mt-4">
                <button className="btn-primary" onClick={closeReopen}>Done</button>
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-500 mb-3">
                  Reopen: it goes back to In Progress and shows in Planning again — every pass of it, even ones printed before.
                </p>
                <label className="label">Reason <span className="text-red-500">*</span></label>
                <textarea className="input w-full h-20" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)}
                          placeholder="Why is this being reopened? e.g. reprint for short quantity" />
                <div className="flex justify-end gap-2 mt-4">
                  <button className="btn-ghost" onClick={closeReopen}>Cancel</button>
                  <button className="btn-primary" disabled={reopenReason.trim().length < 3 || reopenJob.isPending}
                    onClick={() => reopenJob.mutate({ jobId: reopenTarget.id, reason: reopenReason.trim() })}>
                    {reopenJob.isPending ? 'Reopening…' : 'Reopen Job'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {versionsTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-brand">Job Card Versions — <span className="font-mono">{versionsTarget}</span></h2>
              <button className="text-slate-400 hover:text-slate-600 text-xl leading-none" onClick={() => setVersionsTarget(null)}>&times;</button>
            </div>

            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold text-blue-800 text-sm">Current Version</span>
                  <span className="text-xs text-slate-500 ml-2">(latest)</span>
                </div>
                <Link
                  to={`/job-orders/${encodeURIComponent(versionsTarget)}/print`}
                  className="text-brand hover:underline text-sm font-medium"
                  onClick={() => setVersionsTarget(null)}
                >View Card</Link>
              </div>
            </div>

            {versions.isLoading && <p className="text-sm text-slate-500 py-4 text-center">Loading versions…</p>}
            {versions.data && versions.data.length === 0 && <p className="text-sm text-slate-500 py-4 text-center">No previous versions.</p>}

            {versions.data && versions.data.length > 0 && (
              <div className="space-y-2">
                {versions.data.map((v, i) => (
                  <div key={v.History_ID} className="flex items-center justify-between p-3 bg-slate-50 border rounded hover:bg-slate-100">
                    <div>
                      <span className="font-semibold text-sm text-slate-800">V{versions.data!.length - i}</span>
                      <span className="text-xs text-slate-500 ml-2">
                        {v.Changed_On ? new Date(v.Changed_On).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                      <span className="text-xs text-slate-600 ml-2">by {v.UserName || 'System'}</span>
                      {v.Change_Reason && <div className="text-xs text-amber-700 mt-0.5">{v.Change_Reason}</div>}
                    </div>
                    <Link
                      to={`/job-orders/history/${v.History_ID}/print`}
                      className="text-brand hover:underline text-sm font-medium shrink-0 ml-3"
                      onClick={() => setVersionsTarget(null)}
                    >View Card</Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** VPPL: how many of the team's direct edits (no approval) are left this month. */
function DirectEditsLeft() {
  const { data } = useQuery({
    queryKey: ['jc-edit-allowance'],
    queryFn: () => api<{ allowance: { limit: number; used: number; left: number } | null }>('/api/job-orders/edit-allowance'),
    staleTime: 30_000,
  });
  const a = data?.allowance;
  if (!a) return null;
  return (
    <p className={`text-xs ${a.left === 0 ? 'text-red-700' : a.left <= 3 ? 'text-amber-700' : 'text-slate-500'}`}>
      VPPL direct edits this month: <b>{a.left} of {a.limit}</b> left (shared by the team)
      {a.left === 0 && ' - edits now need a request approved by GM'}
    </p>
  );
}

/**
 * Edit, for everyone who edits job cards.  Two ways in:
 *
 *   Club Order / Quantity  attach another PO, or change the quantity and the
 *                          wastage, nothing else.  No approval for anyone; the card before is
 *                          kept in its versions and the print shows no change.
 *   Full edit              Super Admin straight in.  VPPL: with a request GM
 *                          approved, else one of the team's direct edits this
 *                          month (with a reason), else a request to GM.
 */
function EditAction({ jobId, superAdmin }: { jobId: string; superAdmin: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const enc = encodeURIComponent(jobId);

  const { data } = useQuery({
    enabled: !superAdmin,
    queryKey: ['jc-edit-perm', jobId],
    queryFn: () => api<{ canEdit: boolean; hasPending: boolean; reason: string | null; directLeft?: number; directLimit?: number }>(
      `/api/job-orders/${enc}/edit-permission`,
    ),
    staleTime: 30_000,
  });
  const directLeft = data?.directLeft ?? 0;
  const directLimit = data?.directLimit ?? 10;

  const submit = useMutation({
    mutationFn: (v: { reason: string }) =>
      api(`/api/job-orders/${enc}/edit-request`, { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: () => {
      setReason(''); setErr(null);
      qc.invalidateQueries({ queryKey: ['jc-edit-perm', jobId] });
    },
    onError: (e) => setErr((e as Error).message),
  });

  const close = () => { setOpen(false); setReason(''); setErr(null); };
  const openDirect = () => navigate(`/job-orders/${enc}/edit?directReason=${encodeURIComponent(reason.trim())}`);
  const fullLink = <Link to={`/job-orders/${enc}/edit`} className="btn-primary inline-block">Open full edit</Link>;

  let full: React.ReactNode;
  if (superAdmin) {
    full = <><p className="mt-0.5 mb-2 text-xs text-slate-500">Change anything on the card.</p>{fullLink}</>;
  } else if (!data) {
    full = <p className="mt-0.5 text-xs text-slate-500">Checking…</p>;
  } else if (data.canEdit) {
    full = <><p className="mt-0.5 mb-2 text-xs text-slate-500">GM approved a one-time edit of this card.</p>{fullLink}</>;
  } else if (directLeft > 0) {
    // One of the month's direct edits: a reason, then straight to the form.
    full = (
      <>
        <p className="mt-0.5 mb-2 text-xs text-slate-500">
          No approval needed: saving uses one of the VPPL team's direct edits — <b>{directLeft} of {directLimit}</b> left
          this month. GM sees each one with its reason.
        </p>
        <input className="input" maxLength={500} placeholder="What are you changing, and why?"
               value={reason} onChange={(e) => setReason(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter' && reason.trim()) openDirect(); }} />
        <button className="btn-primary mt-2" disabled={!reason.trim()} onClick={openDirect}>Open full edit</button>
      </>
    );
  } else if (data.hasPending) {
    full = <p className="mt-0.5 text-xs text-amber-700">Your request is waiting on GM{data.reason ? <> — <i>{data.reason}</i></> : null}.</p>;
  } else {
    full = (
      <>
        <p className="mt-0.5 mb-2 text-xs text-slate-500">
          The VPPL team has used its {directLimit} direct edits this month. Explain what needs to change and why — GM will decide.
        </p>
        <textarea className="input min-h-[80px]" placeholder="Reason for edit request…"
                  value={reason} onChange={(e) => setReason(e.target.value)} />
        {err && <div className="mt-1 text-sm text-red-700">{err}</div>}
        <button className="btn-primary mt-2" disabled={!reason.trim() || submit.isPending}
                onClick={() => submit.mutate({ reason: reason.trim() })}>
          {submit.isPending ? 'Submitting…' : 'Submit request'}
        </button>
      </>
    );
  }

  return (
    <>
      <button onClick={() => setOpen(true)} className="text-brand hover:underline text-sm">Edit</button>
      {data?.hasPending && <span className="text-amber-700 text-[11px] cursor-help" title={`Waiting on GM. Reason: ${data.reason ?? ''}`}>· request pending</span>}
      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={close}>
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 text-left" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-brand mb-3">Edit job card {jobId.split('/').pop()}</h2>
            <button type="button" onClick={() => navigate(`/job-orders/${enc}/edit?mode=club`)}
                    className="w-full rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-left hover:bg-blue-100">
              <div className="font-semibold text-blue-800">Club Order / Quantity</div>
              <div className="mt-0.5 text-xs text-blue-900">
                Attach another PO, or change the quantity and wastage — nothing else. No approval needed.
                The job card prints with no change shown; the card before is kept in its versions.
              </div>
            </button>
            <div className="mt-4 border-t border-slate-100 pt-4">
              <div className="text-sm font-semibold text-slate-800">Full edit</div>
              {full}
            </div>
            <div className="mt-4 flex justify-end">
              <button className="btn-ghost" onClick={close}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
